import { Deferred, pipe } from '@helo/utils';
import { DataTypeConfigU } from './DataTypeConfig';
import { findDataTypeConfigBreadthFirst } from './DataTypeConfig.utils';
export const mapInputObjectConfig = (dataTypeConfigs, mappers) => (config) => {
    const mapFn = mapInputObjectConfig(dataTypeConfigs, mappers);
    if (config.kind === 'INPUT_OBJECT') {
        const mapper = mappers.find(m => m.sourceType === config.typeName);
        if (!mapper) {
            return {
                ...config,
                fields: config.fields.map(mapFn),
            };
        }
        const destinationConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.destinationType);
        if (!destinationConfig) {
            throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
        }
        if (destinationConfig.kind !== 'INPUT_OBJECT') {
            throw new Error(`Destination type "${mapper.destinationType}" is not an INPUT_OBJECT. It is a ${destinationConfig.kind}`);
        }
        const mappedFields = mapper.fields
            .map(fieldMapper => {
            const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
            if (!destField) {
                return null;
            }
            try {
                const locatedConfig = findDataTypeConfigBreadthFirst(fieldMapper.locator, [])(config);
                const ofType = locatedConfig.kind === 'INPUT_VALUE'
                    ? mapFn(locatedConfig.ofType)
                    : locatedConfig;
                return DataTypeConfigU.createInputValueDataTypeConfig({
                    key: fieldMapper.key,
                    ofType,
                    isNullable: false,
                    directives: [],
                    state: {},
                    meta: {
                        path: [...(config.meta?.path ?? []), ['key', fieldMapper.key]],
                        unmappedPath: locatedConfig.meta?.unmappedPath ??
                            locatedConfig.meta?.path ??
                            [],
                    },
                });
            }
            catch (error) {
                return null;
            }
        })
            .filter((field) => field !== null);
        return DataTypeConfigU.createInputObjectDataTypeConfig({
            typeName: mapper.destinationType,
            fields: mappedFields,
            description: destinationConfig.description,
            isNullable: destinationConfig.isNullable,
            directives: destinationConfig.directives,
            state: pipe(destinationConfig.state ?? {}, Deferred.merge(config.state ?? {})),
            meta: {
                path: config.meta?.path ?? [],
                unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
            },
        });
    }
    switch (config.kind) {
        case 'INPUT_VALUE':
            return {
                ...config,
                ofType: mapFn(config.ofType),
            };
        case 'LIST':
            return {
                ...config,
                ofType: mapFn(config.ofType),
                items: config.items?.map(mapFn),
            };
        case 'FIELD':
            return {
                ...config,
                ofType: mapFn(config.ofType),
            };
        case 'OBJECT':
            return {
                ...config,
                fields: config.fields.map(f => mapFn(f)),
            };
        case 'UNION':
            return {
                ...config,
                possibleTypes: config.possibleTypes.map(mapFn),
            };
        case 'ENUM':
        case 'SCALAR':
        case 'TYPE_REFERENCE':
        case 'ENUM_VALUE':
        default:
            return config;
    }
};
export const reverseMapInputObjectConfig = (dataTypeConfigs, mappers) => (config) => {
    const reverseFn = reverseMapInputObjectConfig(dataTypeConfigs, mappers);
    if (config.kind === 'INPUT_OBJECT') {
        const mapper = mappers.find(m => m.destinationType === config.typeName);
        if (!mapper) {
            return {
                ...config,
                fields: config.fields.map(reverseFn),
            };
        }
        const sourceConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.sourceType);
        if (!sourceConfig) {
            throw new Error(`Source type "${mapper.sourceType}" not found in data type configs`);
        }
        if (sourceConfig.kind !== 'INPUT_OBJECT') {
            throw new Error(`Source type "${mapper.sourceType}" is not an INPUT_OBJECT. It is a ${sourceConfig.kind}`);
        }
        const fieldValueMap = new Map();
        config.fields.forEach(field => {
            fieldValueMap.set(field.key, field.state?.asValue);
        });
        const hasDescendantWithValue = (locator) => {
            return mapper.fields.some(fieldMapper => {
                if (fieldMapper.locator.length <= locator.length)
                    return false;
                const matches = locator.every((matcher, idx) => {
                    const fieldMatcher = fieldMapper.locator[idx];
                    return (fieldMatcher[0] === matcher[0] && fieldMatcher[1] === matcher[1]);
                });
                if (matches) {
                    const fieldValue = fieldValueMap.get(fieldMapper.key);
                    return fieldValue !== undefined && fieldValue !== null;
                }
                return false;
            });
        };
        const setValueAtPath = (baseConfig, locator, value) => {
            if (locator.length === 0) {
                return {
                    ...baseConfig,
                    state: Deferred.merge({ asValue: value })(baseConfig.state ?? {}),
                };
            }
            const [currentMatcher, ...restLocator] = locator;
            const [matcherType, matcherValue] = currentMatcher;
            if (baseConfig.kind === 'INPUT_VALUE') {
                if (matcherType === 'key' && baseConfig.key === matcherValue) {
                    const updatedOfType = setValueAtPath(baseConfig.ofType, restLocator, value);
                    const inputValueValue = updatedOfType.state?.asValue;
                    return {
                        ...baseConfig,
                        ofType: updatedOfType,
                        state: Deferred.merge({ asValue: inputValueValue })(baseConfig.state ?? {}),
                    };
                }
                return baseConfig;
            }
            if (baseConfig.kind === 'INPUT_OBJECT') {
                let wasUpdated = false;
                const fields = baseConfig.fields.map(field => {
                    const updatedField = setValueAtPath(field, locator, value);
                    if (updatedField !== field) {
                        wasUpdated = true;
                    }
                    return updatedField;
                });
                if (!wasUpdated) {
                    return baseConfig;
                }
                const objectValue = {};
                let hasAnyValue = false;
                fields.forEach(field => {
                    if ('key' in field) {
                        const fieldValue = field.state?.asValue;
                        if (fieldValue !== undefined) {
                            objectValue[field.key] = fieldValue;
                            hasAnyValue = true;
                        }
                    }
                });
                return {
                    ...baseConfig,
                    fields: fields,
                    state: Deferred.merge({
                        asValue: hasAnyValue ? objectValue : undefined,
                    })(baseConfig.state ?? {}),
                };
            }
            return baseConfig;
        };
        let rebuiltConfig = {
            ...sourceConfig,
            state: Deferred.merge({})(sourceConfig.state ?? {}),
            meta: {
                path: config.meta?.path ?? [],
                unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
            },
        };
        mapper.fields.forEach(fieldMapper => {
            const value = fieldValueMap.get(fieldMapper.key);
            if (value !== undefined) {
                rebuiltConfig = setValueAtPath(rebuiltConfig, fieldMapper.locator, value);
            }
        });
        rebuiltConfig = {
            ...rebuiltConfig,
            fields: rebuiltConfig.fields.map(reverseFn),
        };
        return rebuiltConfig;
    }
    switch (config.kind) {
        case 'INPUT_VALUE':
            return {
                ...config,
                ofType: reverseFn(config.ofType),
            };
        case 'LIST':
            return {
                ...config,
                ofType: reverseFn(config.ofType),
                items: config.items?.map(reverseFn),
            };
        case 'FIELD':
            return {
                ...config,
                ofType: reverseFn(config.ofType),
            };
        case 'OBJECT':
            return {
                ...config,
                fields: config.fields.map(f => reverseFn(f)),
            };
        case 'UNION':
            return {
                ...config,
                possibleTypes: config.possibleTypes.map(reverseFn),
            };
        case 'ENUM':
        case 'SCALAR':
        case 'TYPE_REFERENCE':
        case 'ENUM_VALUE':
        default:
            return config;
    }
};
export const InputObjectMapperUtils = {
    mapInputObjectConfig,
    reverseMapInputObjectConfig,
};
//# sourceMappingURL=DataTypeConfig-input-mapper.js.map